import { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { InfoCard, Link, Progress } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { scmAuthApiRef } from '@backstage/integration-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useEntityWorkloads } from '../health/api';

const SLUG_ANNOTATION = 'github.com/project-slug';

/**
 * Коммит из тега образа: `production-76e256f9-1788511248` → `76e256f9`. Берём сегмент из 7–8 или
 * 40 hex-символов, в котором есть хотя бы одна буква (иначе это метка времени или версия).
 */
export function commitFromTag(tag: string): string | undefined {
  return tag
    .split(/[-_.]/)
    .find(
      part =>
        /^(?:[0-9a-f]{7,8}|[0-9a-f]{40})$/.test(part) && /[a-f]/.test(part),
    );
}

type CommitInfo = {
  behind?: number;
  date?: string;
  compareUrl?: string;
  error?: string;
};

/** Насколько задеплоенный коммит отстаёт от основной ветки (GitHub compare, токен пользователя). */
function useCommitInfo(slug: string | undefined, commits: string[]) {
  const scmAuthApi = useApi(scmAuthApiRef);
  const [info, setInfo] = useState<Record<string, CommitInfo>>({});
  const key = commits.join(',');

  useEffect(() => {
    if (!slug || !key) return undefined;
    let cancelled = false;
    (async () => {
      const { token } = await scmAuthApi.getCredentials({
        url: `https://github.com/${slug}`,
      });
      const gh = async (path: string) => {
        const res = await fetch(`https://api.github.com/repos/${slug}${path}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`GitHub ${res.status}`);
        return res.json();
      };
      const { default_branch: branch } = await gh('');
      const entries = await Promise.all(
        key.split(',').map(async sha => {
          try {
            const c = await gh(`/compare/${sha}...${branch}`);
            return [
              sha,
              {
                behind: c.ahead_by,
                date: c.base_commit?.commit?.committer?.date,
                compareUrl: c.html_url,
              },
            ] as const;
          } catch (e) {
            return [sha, { error: (e as Error).message }] as const;
          }
        }),
      );
      if (!cancelled) setInfo(Object.fromEntries(entries));
    })().catch(e => {
      if (!cancelled) setInfo({ '*': { error: (e as Error).message } });
    });
    return () => {
      cancelled = true;
    };
  }, [scmAuthApi, slug, key]);

  return info;
}

const useStyles = makeStyles({
  mono: { fontFamily: 'monospace', fontSize: 12 },
});

/**
 * Что сейчас развёрнуто в prod-кластерах: образ каждого ворклоада сущности, коммит из тега и
 * сколько коммитов основной ветки ещё не выкачено.
 */
export const ProductionVersionsCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const slug = entity.metadata.annotations?.[SLUG_ANNOTATION];
  const { value, error, loading } = useEntityWorkloads(
    stringifyEntityRef(entity),
  );

  const rows = (value?.clusters ?? []).flatMap(c =>
    c.workloads.flatMap(w =>
      w.images.map(image => {
        const tag = image.includes(':') ? image.split(':').pop()! : image;
        return { cluster: c.cluster, w, image, tag, sha: commitFromTag(tag) };
      }),
    ),
  );
  const commits = [
    ...new Set(rows.map(r => r.sha).filter((s): s is string => Boolean(s))),
  ];
  const info = useCommitInfo(slug, commits);

  const lag = (sha?: string) => {
    if (!sha) return '—';
    const i = info[sha] ?? info['*'];
    if (!i) return '…';
    if (i.error) return i.error;
    if (i.behind === 0) return 'актуально';
    return <Link to={i.compareUrl!}>не выкачено коммитов: {i.behind}</Link>;
  };

  return (
    <InfoCard title="Что в проде">
      {loading && <Progress />}
      {error && <Typography color="error">{error.message}</Typography>}
      {value && rows.length === 0 && (
        <Typography color="textSecondary">
          Ворклоадов в кластерах нет.
        </Typography>
      )}
      {rows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Кластер</TableCell>
              <TableCell>Ворклоад</TableCell>
              <TableCell>Реплики</TableCell>
              <TableCell>Образ</TableCell>
              <TableCell>Коммит</TableCell>
              <TableCell>Основная ветка</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.cluster}</TableCell>
                <TableCell>{r.w.name}</TableCell>
                <TableCell>
                  {r.w.ready}/{r.w.desired ?? '—'}
                </TableCell>
                <TableCell className={classes.mono}>{r.tag}</TableCell>
                <TableCell className={classes.mono}>
                  {r.sha && slug ? (
                    <Link to={`https://github.com/${slug}/commit/${r.sha}`}>
                      {r.sha}
                    </Link>
                  ) : (
                    '—'
                  )}
                  {r.sha && info[r.sha]?.date && (
                    <Typography variant="caption" display="block">
                      {new Date(info[r.sha].date!).toLocaleDateString()}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{lag(r.sha)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </InfoCard>
  );
};
